import esri = __esri;

import { property, subclass } from "esri/core/accessorSupport/decorators";
import watchUtils = require("esri/core/watchUtils");
import Widget = require("esri/widgets/Widget");
import * as layerSource from "../data/Layers/layerSource";

import { renderable, tsx } from "esri/widgets/support/widget";

import { Extent } from "esri/geometry";
import MapView = require("esri/views/MapView");

import AddPushPinTool from "./Toolbar/addPushPinTool";
import ClearGraphicsTool from "./Toolbar/clearGraphicsTool";
import ToggleResultsTool from "./Toolbar/toggleResultsTool";
import DataReportTool from "./Toolbar/dataReportTool";
import GetRouteStationingData from "./Toolbar/getRouteStationingData";
import FullExtentTool from "./Toolbar/fullExtentTool";
import FullScreenTool from "./Toolbar/fullScreenTool";
import MeasureAreaTool from "./Toolbar/measureAreaTool";
import MeasureDistanceTool from "./Toolbar/measureDistanceTool";
import MeasureRouteDistanceTool from "./Toolbar/measureRouteDistanceTool";
import PanTool from "./Toolbar/panTool";
import SelectByPolygonTool from "./Toolbar/selectByPolygonTool";
import SelectByRectangleTool from "./Toolbar/selectByRectangleTool";
import ToolButton from "./Toolbar/toolButton";
import VanPicsTool from "./Toolbar/vanPicsTool";
import VanPicsTool2 from "./Toolbar/vanPicsTool";
import ViewInBingTool from "./Toolbar/viewInBingTool";
import ViewInGoogleTool from "./Toolbar/viewInGoogleTool";
import ZoomInTool from "./Toolbar/zoomInTool";
import ZoomOutTool from "./Toolbar/zoomOutTool";
import { renameTooltips, closeExpanded } from './App/setupView';

interface ToolbarParams extends esri.WidgetProperties {
  view?: esri.MapViewProperties | esri.SceneViewProperties;
}

const CSS = {
  base: "toolbar-widget"
};

const maxExtents = 30;
let curExtent = -1; // if -1, then not using extents.
const extentList: Extent[] = [];

@subclass("esri.widgets.ToolBar")
class ToolBar extends Widget {
  // --------------------------------------------------------------------
  //
  //  Properties
  //
  // --------------------------------------------------------------------

  // ----------------------------------
  //  view
  // ----------------------------------

  @property()
  @renderable()
  view: MapView;

  // -------------------------------------------------------------------
  //
  //  Private methods
  //
  // -------------------------------------------------------------------


  measureAreaTool: MeasureAreaTool;
  measureDistanceTool: MeasureDistanceTool;
  measureRouteDistanceTool: MeasureRouteDistanceTool;
  clearGraphicsTool: ClearGraphicsTool;
  toggleResultsTool: ToggleResultsTool;
  dataReportTool: DataReportTool;
  getRouteStationingData: GetRouteStationingData;
  vanPicsTool: VanPicsTool;
  vanPicsTool2: VanPicsTool;
  fullExtentTool: FullExtentTool;
  fullScreenTool: FullScreenTool;
  panTool: PanTool;
  addPushPinTool: AddPushPinTool;
  selectByPolygonTool: SelectByPolygonTool;
  selectByRectangleTool: SelectByRectangleTool;
  viewInBingTool: ViewInBingTool;
  viewInGoogleTool: ViewInGoogleTool;
  zoomInTool: ZoomInTool;
  zoomOutTool: ZoomOutTool;
  
  

  //   let activeTool:any = null;
  activeTool: ToolButton;

  constructor(params: Partial<ToolbarParams>) {
    super(params);

    this.measureAreaTool = new MeasureAreaTool(params.view as MapView, this);
    this.measureDistanceTool = new MeasureDistanceTool(params.view as MapView, this);
    this.measureRouteDistanceTool = new MeasureRouteDistanceTool(params.view as MapView, this);

    this.clearGraphicsTool = new ClearGraphicsTool(params.view as MapView, this);
    this.toggleResultsTool = new ToggleResultsTool(params.view as MapView, this);
    this.dataReportTool = new DataReportTool(params.view as MapView, this);
    this.getRouteStationingData = new GetRouteStationingData(params.view as MapView, this);
    this.vanPicsTool = new VanPicsTool(params.view as MapView, this);
    this.vanPicsTool2 = new VanPicsTool2(params.view as MapView, this);
    this.fullExtentTool = new FullExtentTool(params.view as MapView, this);
    this.fullScreenTool = new FullScreenTool(params.view as MapView, this);
    this.panTool = new PanTool(params.view as MapView, this);
    this.addPushPinTool = new AddPushPinTool(params.view as MapView, this);
    this.selectByPolygonTool = new SelectByPolygonTool(params.view as MapView, this);
    this.selectByRectangleTool = new SelectByRectangleTool(params.view as MapView, this);
    this.viewInBingTool = new ViewInBingTool(params.view as MapView, this);
    this.viewInGoogleTool = new ViewInGoogleTool(params.view as MapView, this);
    this.zoomInTool = new ZoomInTool(params.view as MapView, this);
    this.zoomOutTool = new ZoomOutTool(params.view as MapView, this);

    this._onViewChange = this._onViewChange.bind(this);
  }

  postInitialize() {
    watchUtils.init(this, "view.center, view.interacting, view.scale, view.stationary", () => this._onViewChange());
  }

  // -------------------------------------------------------------------
  //
  //  Public methods
  //
  // -------------------------------------------------------------------

  render() {
    return (
      <div class={CSS.base}>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.zoomInTool}
          onclick={this.zoomInTool.activateTool}
          role="button"
          tabindex="0"
          title="Zoom In"
        >
          <img class="toolbar_img" src="./assets/plus-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.zoomOutTool}
          onclick={this.zoomOutTool.activateTool}
          role="button"
          tabindex="0"
          title="Zoom Out"
        >
          <img class="toolbar_img" src="./assets/minus-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.panTool}
          onclick={this.panTool.activateTool}
          role="button"
          id="PanTool"
          tabindex="0"
          aria-label="Pan"
          title="Pan"
        >
          <span aria-hidden="true" role="presentation" class="esri-icon">
            <img src="./assets/pan.png" alt="Search" height="24" />
          </span>
          <span class="esri-icon-font-fallback-text">Pan</span>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          role="button"
          tabindex="0"
          aria-label="Previous Extent"
          bind={this}
          onclick={this._previousExtentTool}
          title="Previous Extent"
        >
          <img class="toolbar_img" src="./assets/chevron-left-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          role="button"
          tabindex="0"
          aria-label="Next Extent"
          bind={this}
          onclick={this._nextExtentTool}
          title="Next Extent"
        >
          <img class="toolbar_img" src="./assets/chevron-right-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          role="button"
          tabindex="0"
          aria-label="Full Extent"
          bind={this.fullExtentTool}
          onclick={this.fullExtentTool.activateTool}
          title="Full Extent"
        >
          <img class="toolbar_img" src="./assets/extent-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          role="button"
          tabindex="0"
          aria-label="Full Screen"
          bind={this.fullScreenTool}
          onclick={this.fullScreenTool.activateTool}
          title="Full Screen"
        >
          <img class="toolbar_img" src="./assets/expand-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          role="button"
          tabindex="0"
          aria-label="Measure Distance"
          bind={this.measureDistanceTool}
          onclick={this.measureDistanceTool.activateTool}
          title="Measure Distance"
        >
          <img class="toolbar_img" src="./assets/measure-line-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          role="button"
          tabindex="0"
          aria-label="Measure Area"
          bind={this.measureAreaTool}
          onclick={this.measureAreaTool.activateTool}
          title="Measure Area"
        >
          <img class="toolbar_img" src="./assets/measure-area-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          role="button"
          tabindex="0"
          aria-label="Measure Route Distance"
          bind={this.measureRouteDistanceTool}
          onclick={this.measureRouteDistanceTool.activateTool}
          title="Measure Route Distance"
        >
          <img class="toolbar_img" src="./assets/measure-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.getRouteStationingData}
          onclick={this.getRouteStationingData.activateTool}
          role="button"
          tabindex="0"
          aria-label="Get Route Stationing Data"
          title="Get Route Stationing Data"
        >
          <img class="toolbar_img" src="./assets/gauge-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          role="button"
          tabindex="0"
          aria-label="Select by Polygon"
          bind={this.selectByPolygonTool}
          onclick={this.selectByPolygonTool.activateTool}
          title="Select by Polygon"
        >
          <img class="toolbar_img" src="./assets/polygon-plus-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          role="button"
          tabindex="0"
          aria-label="Select by Rectangle"
          bind={this.selectByRectangleTool}
          onclick={this.selectByRectangleTool.activateTool}
          title="Select by Rectangle"
        >
          <img class="toolbar_img" src="./assets/rectangle-plus-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.toggleResultsTool}
          onclick={this.toggleResultsTool.activateTool}
          role="button"
          tabindex="0"
          aria-label="Toggle Results Grid"
          title="Toggle Results Grid"
        >
          <img class="toolbar_img" src="./assets/table-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.clearGraphicsTool}
          onclick={this.clearGraphicsTool.activateTool}
          role="button"
          tabindex="0"
          aria-label="Clear Graphics and Results Grid"
          title="Clear Graphics and Results Grid"
        >
          <img class="toolbar_img" src="./assets/erase-24.svg"/>
        </div>
        {/* Not using identify for now, just click on map 
               <div class="esri-widget--button esri-widget toolbar_icon" 
               bind={zoomInTool}
               onclick={zoomInTool.activateTool}              
                role="button" 
                tabindex="0" 
                aria-label="Identify Tool"
                title="Identify Tool">
                <span aria-hidden="true" 
                    class="esri-icon-description"></span>
                    <span class="esri-icon-font-fallback-text">Identify Tool
                </span>
            </div>             */}
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.vanPicsTool}
          onclick={this.vanPicsTool.activateTool}
          role="button"
          tabindex="0"
          aria-label="Van Pictures Tool"
          title="Van Pictures Tool"
        >
          <img class="toolbar_img" src="./assets/car-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.vanPicsTool2}
          onclick={this.vanPicsTool2.activateTool}
          role="button"
          tabindex="0"
          aria-label="IDOT Van Pictures"
          title="IDOT Van Pictures"
        >
          <img class="toolbar_img" src="./assets/diamond-inset-large-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.dataReportTool}
          onclick={this.dataReportTool.activateTool}
          role="button"
          tabindex="0"
          aria-label="Data Report Tool"
          title="Data Report Tool"
        >
          <img class="toolbar_img" src="./assets/graph-bar-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.addPushPinTool}
          onclick={this.addPushPinTool.activateTool}
          role="button"
          tabindex="0"
          aria-label="Add Push Pin to Map"
          title="Add Push Pin to Map"
        >
          <img class="toolbar_img" src="./assets/pushpin-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.viewInGoogleTool}
          onclick={this.viewInGoogleTool.activateTool}
          role="button"
          tabindex="0"
          aria-label="Zoom to Google"
          title="Zoom to Google"
        >
          <img class="toolbar_img" src="./assets/globe-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this.viewInBingTool}
          onclick={this.viewInBingTool.activateTool}
          role="button"
          tabindex="0"
          aria-label="Zoom to Bing"
          title="Zoom to Bing"
        >
          <img class="toolbar_img" src="./assets/map-24.svg"/>
        </div>
        <div
          class="esri-widget--button esri-widget toolbar_icon"
          bind={this}
          onclick={this._showHelpTool}
          role="button"
          tabindex="0"
          aria-label="Help"
          title="Help"
        >
          <img class="toolbar_img" src="./assets/question-mark-24.svg"/>
        </div>
      </div>
    );
  }

  setPanAsActiveTool() {
    if (this.panTool) {
      this.setActiveTool(this.panTool);
      if (this.panTool.iconDiv) {
        this.panTool.iconDiv.focus();
      }
    }
  }

  setActiveTool(tool: ToolButton) {
    closeExpanded();
    // console.log("\n\nin setActiveTool : ", tool);
    if (this.activeTool && this.activeTool.deactivateTool) {
      if (this.activeTool.iconDiv) {
        this.activeTool.iconDiv.style.backgroundColor = "";
      }
      this.activeTool.deactivateTool();
    }
    if (tool.iconDiv) {
      tool.iconDiv.style.backgroundColor = "#DEEEFF";
    }
    this.activeTool = tool;
  }

  private _previousExtentTool() {
    if (extentList.length > 0) {
      if (curExtent === -1) {
        curExtent = extentList.length - 1;
      }
      if (curExtent > 0 && curExtent < extentList.length) {
        curExtent = curExtent - 1;
        this.view.goTo(extentList[curExtent]);
        // console.log ("Going back extent# ", curExtent, " of ", extentList.length);
      }
    }
  }

  private _showHelpTool() {
    const helpUrl = './assets/Illinois Roadway Analysis Database System.pdf';
    window.open(helpUrl);
    this.setPanAsActiveTool();
  }

  private _nextExtentTool() {
    if (extentList.length > 0) {
      if (curExtent >= 0 && curExtent < extentList.length - 1) {
        curExtent = curExtent + 1;
        this.view.goTo(extentList[curExtent]);
        // console.log ("Going forward extent# ", curExtent, " of ", extentList.length);
      }
    }
  }

  private _addExtent(extent: Extent) {
    if (extentList.length > 0) {
      let lastExtent;
      curExtent === -1 ? (lastExtent = extentList[extentList.length - 1]) : (lastExtent = extentList[curExtent]);
      if (
        lastExtent.xmin !== extent.xmin &&
        lastExtent.ymin !== extent.ymin &&
        lastExtent.xmax !== extent.xmax &&
        lastExtent.ymax !== extent.ymax
      ) {
        if (extentList.length >= maxExtents) {
          extentList.splice(0, 1); // delete first extent
        }

        extentList.push(extent);
        // console.log("New Extent ", extentList.length, " bbox : ", extent.xmin, extent.ymin, extent.xmax, extent.ymax);
        curExtent = -1; // reset curExtent
      }
    } else {
      extentList.push(extent);
    }
  }

  private _onViewChange() {
    if (!this.panTool.iconDiv) {
      const div = document.getElementById("PanTool");
      if (div) {
        this.panTool.iconDiv = div;
        this.setPanAsActiveTool();
      }
    }
    if (this.view) {
      renameTooltips();
      const { center, interacting, stationary } = this.view;
      if (center) {
        // console.log("Interacting = ", interacting, " Stationary = ", stationary);
        if (interacting === false && stationary === true) {
          this._addExtent(this.view.extent);
          if (layerSource.serverUrl && layerSource.serverUrl.search("webapps.dot.illinois.gov") < 0) {
            const vScale = document.getElementById('vScale');
            if (vScale) {
              const scale = this.view.scale > 9999 ?  Math.round(this.view.scale / 1000) * 1000 : Math.round(this.view.scale);
              vScale.innerHTML = '1:' + scale.toLocaleString();
              // console.log("Scale 1:", scale.toLocaleString());
            }
          }
        }
      }
    }
  }
}

export = ToolBar;
